<template>
  <div
    aria-label="Formulaire de création de bon de retour logistique"
    role="form"
  >
    <div
      v-if="isLoading === true"
      class="text-center mb-2"
      aria-busy="true"
      aria-live="polite"
    >
      <b-spinner
        variant="primary"
        label="Chargement en cours..."
        aria-label="Chargement en cours..."
      />
    </div>
    <b-card>
      <b-form-group
        label="Fournisseur *"
        aria-label="Sélection du fournisseur logistique (obligatoire)"
        description="Sélectionnez le fournisseur concerné par le retour."
      >
        <validation-provider
          #default="{ errors }"
          name="Fournisseur"
        >
          <v-select
            v-model="supplier"
            :clearable="false"
            placeholder="Sélectionner un fournisseur logistique"
            label="username"
            :options="suppliers"
            aria-label="Liste déroulante des fournisseurs logistiques"
            aria-required="true"
            @input="getOrders"
          />
          <small class="text-danger">{{ errors[0] }}</small>
        </validation-provider>
      </b-form-group>
      <b-form-group
        label="Référence de colis *"
        label-for="vi-first-name"
        aria-label="Saisir la référence du colis à retourner (obligatoire)"
        description="Saisissez ou scannez la référence du colis à ajouter au bon de retour."
      >
        <b-input-group class="input-group-merge">
          <b-input-group-prepend is-text>
            <feather-icon
              icon="MaximizeIcon"
              aria-hidden="true"
            />
          </b-input-group-prepend>
          <b-form-input
            ref="select"
            v-model="ref"
            placeholder="Saisir ou scanner la référence du colis"
            aria-label="Champ de saisie de la référence du colis à retourner"
            aria-required="true"
            @change="checkOrder"
          />
        </b-input-group>
      </b-form-group>
    </b-card>

    <!-- table -->
    <div class="d-flex justify-content-between">
      <b-card>
        <h4>retour au depot : {{ orders.length }}</h4>
        <vue-good-table
          :columns="columns"
          :rows="processedOrders"
          :search-options="{
            enabled: true,
            externalQuery: searchTerm }"
        >
          <template
            slot="table-row"
            slot-scope="props"
          >
            <span v-if="props.column.field === 'id'">
              <span> {{ props.row.id }}</span><br>
              <span
                v-if="props.row.is_an_exchange"
                style="color:red ; font-weight: bold;"
              > Echange </span>
            </span>
            <!-- Column: Action -->
            <span v-else-if="props.column.field === 'action'">
              <span>
                <feather-icon
                  :id="`invoice-row-${props.row.id}-show-icon`"
                  icon="CheckSquareIcon"
                  class="cursor-pointer mr-1"
                  size="16"
                  color="green"
                  @click="addItem(props.row)"
                />
                <b-tooltip
                  title="Ajouter"
                  class="cursor-pointer"
                  :target="`invoice-row-${props.row.id}-show-icon`"
                />

              </span>
            </span>

            <!-- Column: Common -->

            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>

          <!-- pagination -->
          <template
            slot="pagination-bottom"
            slot-scope="props"
          >
            <div class="d-flex justify-content-between flex-wrap">
              <div class="d-flex align-items-center mb-0 mt-1">
                <span class="text-nowrap">
                  Affichage 1 à
                </span>
                <b-form-select
                  v-model="pageLength"
                  :options="['50','100','500']"
                  class="mx-1"
                  @input="(value)=>props.perPageChanged({currentPerPage:value})"
                />
                <span class="text-nowrap "> de {{ props.total }} résultats trouvés </span>
              </div>
              <div>
                <b-pagination
                  :value="1"
                  :total-rows="props.total"
                  :per-page="pageLength"
                  first-number
                  last-number
                  align="right"
                  prev-class="prev-item"
                  next-class="next-item"
                  class="mt-1 mb-0"
                  @input="(value)=>props.pageChanged({currentPage:value})"
                >
                  <template #prev-text>
                    <feather-icon
                      icon="ChevronLeftIcon"
                      size="18"
                    />
                  </template>
                  <template #next-text>
                    <feather-icon
                      icon="ChevronRightIcon"
                      size="18"
                    />
                  </template>
                </b-pagination>
              </div>
            </div>
          </template>
        </vue-good-table>
      </b-card>
      <b-card class="ml-1">
        <h4>retour au fournisseur : {{ selected.length }}</h4>
        <vue-good-table
          :columns="columns"
          :rows="selected"
          :search-options="{
            enabled: true,
            externalQuery: searchTerm }"
        >
          <template
            slot="table-row"
            slot-scope="props"
          >
            <span v-if="props.column.field === 'id'">
              <span> {{ props.row.id }}</span><br>
              <span
                v-if="props.row.is_an_exchange"
                style="color:red ; font-weight: bold;"
              > Echange </span>
            </span>
            <!-- Column: Action -->
            <span v-else-if="props.column.field === 'action'">
              <span>
                <feather-icon
                  :id="`invoice-row-${props.row.id}-show-icon`"
                  icon="DeleteIcon"
                  color="red"
                  class="cursor-pointer mr-1"
                  size="16"
                  @click="resetItem(props.row)"
                />
                <b-tooltip
                  title="Annuler"
                  class="cursor-pointer"
                  :target="`invoice-row-${props.row.id}-show-icon`"
                />

              </span>
            </span>

            <!-- Column: Common -->

            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>

          <!-- pagination -->
          <template
            slot="pagination-bottom"
            slot-scope="props"
          >
            <div class="d-flex justify-content-between flex-wrap">
              <div class="d-flex align-items-center mb-0 mt-1">
                <span class="text-nowrap">
                  Affichage 1 à
                </span>
                <b-form-select
                  v-model="pageLength"
                  :options="['50','100','500']"
                  class="mx-1"
                  @input="(value)=>props.perPageChanged({currentPerPage:value})"
                />
                <span class="text-nowrap "> de {{ props.total }} résultats trouvés </span>
              </div>
              <div>
                <b-pagination
                  :value="1"
                  :total-rows="props.total"
                  :per-page="pageLength"
                  first-number
                  last-number
                  align="right"
                  prev-class="prev-item"
                  next-class="next-item"
                  class="mt-1 mb-0"
                  @input="(value)=>props.pageChanged({currentPage:value})"
                >
                  <template #prev-text>
                    <feather-icon
                      icon="ChevronLeftIcon"
                      size="18"
                    />
                  </template>
                  <template #next-text>
                    <feather-icon
                      icon="ChevronRightIcon"
                      size="18"
                    />
                  </template>
                </b-pagination>
              </div>
            </div>
          </template>

        </vue-good-table>

        <b-form-group class="d-flex justify-content-end">
          <span id="save-return-voucher-btn-wrapper">
            <b-button
              variant="gradient-primary"
              class="ml-1 d-inline-block mt-1"
              :class="{ 'disabled-cursor': isLoading || !isReturnVoucherValid }"
              :disabled="isLoading || !isReturnVoucherValid"
              @click="addReturnVoucher"
            >
              <feather-icon icon="PlusIcon" />
              <span class="ml-1">Enregistrer</span>
            </b-button>
            <b-tooltip
              v-if="!isReturnVoucherValid"
              target="save-return-voucher-btn-wrapper"
              triggers="hover focus"
              placement="top"
            >
              <template v-if="supplier.id === ''">
                Sélectionnez un fournisseur logistique pour activer l'enregistrement.
              </template>
              <template v-else-if="selected.length === 0">
                Ajoutez au moins un colis à retourner pour activer l'enregistrement.
              </template>
            </b-tooltip>
          </span>
        </b-form-group>
      </b-card>

    </div>
  </div>
</template>

<script>
import {
  BCard, BButton, BPagination, BFormGroup, BFormInput, BInputGroup, BInputGroupPrepend, BFormSelect, BTooltip,

} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import Ripple from 'vue-ripple-directive'
import axios from 'axios'
import { orderMatchesReference } from '@/utils/orderReference'
import vSelect from 'vue-select'
import { ValidationProvider } from 'vee-validate'
import storeAuth from '@/store/store'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import ok from '@/assets/audio/ok.mp3'
import notexist from '@/assets/audio/notexist.mp3'
import isAnExchange from '@/assets/audio/exchange.mp3'

export default {
  components: {
    BButton,
    ValidationProvider,
    VueGoodTable,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BFormSelect,
    BPagination,
    BTooltip,
    vSelect,
    BCard,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      suppliers: [],
      supplier: '',
      date: '01-10-2021',
      status: '',
      pageLength: 100,
      columns: [
        {
          label: 'Action',
          field: 'action',
        },
        {
          label: 'BL',
          field: 'id',
          filterOptions: {
            enabled: true,
            placeholder: 'BL',
          },
        },
        {
          label: 'Status',
          field: 'status',
          filterOptions: {
            enabled: true,
            placeholder: 'Status',
          },
        },
        {
          label: 'Client',
          field: 'name',
          filterOptions: {
            enabled: true,
            placeholder: 'Client',
          },
        },
        {
          label: 'Gouvernorat',
          field: 'governorate',
          filterOptions: {
            enabled: true,
            placeholder: 'Gouvernorat',
          },
        },
        {
          label: 'Motifs de retour',
          field: 'motifs_retour',
        },
      ],
      orders: [],
      selected: [],
      searchTerm: '',
      ref: '',
      user: {},
      isLoading: false,
      ok,
      notexist,
      isAnExchange,
    }
  },
  computed: {
    isReturnVoucherValid() {
      return (
        this.supplier.id !== ''
        && this.selected.length > 0
      )
    },
    processedOrders() {
      // Status mapping for allowed statuses
      const statusMap = {
        2: 'Colis au dépôt',
        5: 'Retour au dépôt',
        10: 'Retour inter-agence',
        11: 'Retour inter agency reçu',
        12: 'Colis annulé par admin',
        18: 'Planification',
        22: 'Retour définitif',
      }
      return this.orders.map(order => {
        const processedOrder = { ...order }
        // Map all_return_reasons to a displayable string
        if (order.all_return_reasons && order.all_return_reasons.length) {
          processedOrder.motifs_retour = order.all_return_reasons.map(reason => {
            let str = reason.return_reason
            if (reason.created_at) str += ` (le ${this.formatFn ? this.formatFn(reason.created_at) : reason.created_at})`
            if (reason.scheduled_date) str += `, planifié: ${this.formatFn ? this.formatFn(reason.scheduled_date) : reason.scheduled_date}`
            if (reason.additional_note) str += `, note: ${reason.additional_note}`
            if (reason.quick_options && reason.quick_options.length) str += `, options: ${reason.quick_options.join(', ')}`
            return str
          }).join('\n')
        } else {
          processedOrder.motifs_retour = ''
        }
        // Map status to readable string for allowed statuses
        processedOrder.status = statusMap[order.status] || order.status
        return processedOrder
      })
    },
  },
  created() {
    this.user = storeAuth.state.user
    this.getSuppliers()
  },
  mounted() {
    this.$refs.select.focus()
  },
  methods: {
    async playAudio(payload) {
      const voice = new Audio(payload)
      voice.addEventListener('canplaythrough', () => {
        voice.play()
      })
    },
    async withTitle(text) {
      try {
        await this.$swal({
          title: 'Alerte',
          icon: 'warning',
          text,
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        })
        this.ref = ''
      } catch (e) {
        this.ref = ''
      }
    },
    async checkOrder() {
      const result = this.orders.find(element => orderMatchesReference(element, this.ref))
      if (result) {
        if (result.is_an_exchange) {
          await this.playAudio(this.isAnExchange)
        }
        await this.addItem(result)
        this.ref = ''
        await this.playAudio(this.ok)
      } else {
        await this.withTitle('Colis non exist')
        await this.playAudio(this.notexist)
      }
    },
    async getOrders() {
      if (this.supplier !== '') {
        this.isLoading = true
        const { data } = await axios.get(`api/orders/get-orders-allowed-to-be-returned-definitely/${this.supplier.id}`)
        this.orders = data
        this.selected = []
        this.isLoading = false
      }
    },
    async addReturnVoucher() {
      // Build a summary of selected orders
      let orderSummary = ''
      if (this.selected.length > 0) {
        orderSummary = `<ul style="text-align:left">${this.selected.map(order => {
          const statusMap = {
            2: 'Colis au dépôt',
            5: 'Retour au dépôt',
            10: 'Retour inter-agence',
            11: 'Retour inter agency reçu',
            12: 'Colis annulé par admin',
            14: 'À vérifier',
            18: 'Planification',
            22: 'Retour définitif',
          }
          let motifs = ''
          if (order.all_return_reasons && order.all_return_reasons.length) {
            motifs = order.all_return_reasons.map(reason => {
              let str = reason.return_reason
              if (reason.created_at) str += ` (le ${this.formatFn ? this.formatFn(reason.created_at) : reason.created_at})`
              if (reason.scheduled_date) str += `, planifié: ${this.formatFn ? this.formatFn(reason.scheduled_date) : reason.scheduled_date}`
              if (reason.additional_note) str += `, note: ${reason.additional_note}`
              if (reason.quick_options && reason.quick_options.length) str += `, options: ${reason.quick_options.join(', ')}`
              return str
            }).join('<br>')
          }
          return `<li><b>BL:</b> ${order.id} | <b>Status:</b> ${statusMap[order.status] || order.status} | <b>Client:</b> ${order.name}${motifs ? ` | <b>Motifs:</b> ${motifs}` : ''}</li>`
        }).join('')}</ul>`
      }
      const confirmed = await this.$swal({
        title: 'Confirmation',
        html: `<div>Êtes-vous sûr de vouloir créer ce bon de retour ? Cette action est irréversible.<br><br><b>Commandes concernées :</b>${orderSummary}</div>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Oui, créer',
        cancelButtonText: 'Annuler',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      })
      if (!confirmed.isConfirmed) return
      const obj = {
        supplier: this.supplier.id,
        orders: this.selected,
        repository: this.user.repository,
        user: this.user,
      }
      try {
        this.isLoading = true
        const { data } = await axios.post('/api/return-vouchers/create-return-voucher/', obj)
        this.showToast('success', 'top-center', 'Bon de retour ajouté avec succés')
        this.$router.push(`/bons-retours/show/${data}`)
        this.isLoading = false
      } catch (error) {
        this.showToast('danger', 'top-center', error.toString())
      }
    },
    async getSuppliers() {
      this.isLoading = true
      try {
        const { data } = await this.$http.get('/api/auth/users/suppliers-with-returnable-orders/')
        this.suppliers = data

        // Optional: Show message if no suppliers have returnable orders
        if (this.suppliers.length === 0) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Information',
              icon: 'InfoIcon',
              text: "Aucun fournisseur n'a de commandes éligibles pour retour.",
              variant: 'info',
            },
          })
        }
      } catch (error) {
        console.error('Error fetching suppliers:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erreur',
            icon: 'InfoIcon',
            text: 'Erreur lors du chargement des fournisseurs',
            variant: 'danger',
          },
        })
      } finally {
        this.isLoading = false
      }
    },
    async addItem(item) {
      this.selected.push(item)
      const i = this.orders.findIndex(element => {
        if (element.supplier.id === 504) {
          return element.bestway_barcode === item.bestway_barcode
        }
        return element.id === item.id
      })
      this.orders.splice(i, 1)
    },
    async resetItem(item) {
      this.orders.push(item)
      const i = this.selected.findIndex(element => {
        if (element.supplier.id === 504) {
          return element.bestway_barcode === item.bestway_barcode
        }
        return element.id === item.id
      })
      this.selected.splice(i, 1)
    },
    showToast(variant, position, text) {
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            title: 'Notification',
            icon: 'InfoIcon',
            text,
            variant,
          },
        },
        {
          position,
        },
      )
    },
  },
}
</script>

<style lang="scss" >
@import "@core/scss/vue/libs/vue-select.scss";
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
